import React from 'react'
import { 
  DocumentTextIcon,
  CurrencyRupeeIcon,
  EyeSlashIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  IdentificationIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'

const MITCPage: React.FC = () => {
  const sections = [
    {
      icon: DocumentTextIcon,
      title: '1. Scope of Services',
      content: [
        'Analyzerview Market Research ("RA") provides only research reports, analysis, and recommendations.',
        'The RA cannot execute trades (buy/sell) on behalf of clients. Clients are strictly advised not to permit RA to execute any trade on their behalf.'
      ]
    },
    {
      icon: CurrencyRupeeIcon,
      title: '2. Fees & Charges',
      content: [
        'The fees charged to individual/HUF clients shall not exceed the maximum limit prescribed by SEBI/RAASB.',
        'Current fee limit: ₹1,51,000/- per annum per client family (for all research services).',
        'This limit excludes statutory charges.',
        'The fee limit does not apply to non-individual clients / accredited investors.',
        'Fees may be charged in advance, subject to SEBI\'s permitted limits (currently up to 1 quarter in advance).',
        'In case of premature termination of services, the client is entitled to a refund of proportionate fees for the unexpired period.',
        'If RA\'s registration is suspended/cancelled by SEBI, services shall be discontinued, and residual fees will be refunded on a pro-rata basis.',
        'Payment methods: cheque, bank transfer, UPI, or other approved channels. Cash payments are not allowed. Optionally, clients may use the Centralized Fee Collection Mechanism (CeFCoM) managed by BSE (RAASB).'
      ]
    },
    {
      icon: EyeSlashIcon,
      title: '3. Conflict of Interest',
      content: [
        'The RA shall abide by SEBI/RAASB rules on disclosure and mitigation of conflicts of interest.',
        'Any conflict that may affect services will be promptly disclosed to the client.'
      ]
    },
    {
      icon: XCircleIcon,
      title: '4. Prohibition of Assured Returns',
      content: [
        'Assured/guaranteed/fixed return schemes are strictly prohibited.',
        'The RA cannot guarantee returns, profits, accuracy, or risk-free investments from research services.'
      ]
    },
    {
      icon: ExclamationTriangleIcon,
      title: '5. Market Risk Disclaimer',
      content: [
        'Investments based on RA\'s research reports are subject to market risks.',
        'Recommendations do not guarantee returns.',
        'The client bears the responsibility for investment decisions and cannot claim losses from reliance on RA\'s reports.',
        'SEBI registration, RAASB enlistment, or NISM certification does not guarantee performance or returns.'
      ]
    },
    {
      icon: CpuChipIcon,
      title: '6. Artificial Intelligence (AI) Tools',
      content: [
        'To the extent AI tools are used in research analysis or report preparation, disclosures will be made in each research report.'
      ]
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: '7. Grievance Redressal',
      content: [
        'Step 1: Client may first contact Analyzerview Market Research for resolution at:',
        '📧 Email: rahul@analyzerview.com',
        '📞 Phone: +91 93820 93168',
        '🏢 Address: Kukshimla, Raidogachia, Purbasthali, Purba Bardhaman, West Bengal – 713513',
        'Step 2: If unresolved, clients can lodge complaints on SEBI\'s SCORES portal (www.scores.sebi.gov.in).',
        'Step 3: Clients may also opt for Online Dispute Resolution (ODR) via the Smart ODR portal (https://smartodr.in).'
      ]
    },
    {
      icon: UserIcon,
      title: '8. Client Responsibilities',
      content: [
        'Clients must keep contact details (email ID and mobile number) updated with the RA.',
        'Clients should never share trading account, demat account, or bank account credentials/OTPs with RA or any third party. The RA will never request such information.'
      ]
    },
    {
      icon: IdentificationIcon,
      title: '9. Client KYC & Documentation',
      content: [
        'Clients must provide complete and accurate KYC information, including PAN, proof of identity, address, and other details required by SEBI.',
        'RA will collect, verify, and upload KYC details with KRA (KYC Registration Agency) as mandated by SEBI.',
        'Clients must maintain updated contact details (email, phone).',
        'Services may not be provided until KYC is completed and verified.'
      ]
    },
    {
      icon: CheckCircleIcon,
      title: '10. Client Acknowledgment & Consent',
      content: [
        'By subscribing to Analyzerview Market Research services, the client confirms:',
        '• Understanding and acceptance of MITC and all terms & conditions.',
        '• Research reports are used solely for personal investment decisions.',
        '• Responsibility for investment outcomes rests entirely with the client.',
        '• RA cannot guarantee profits or risk-free returns.',
        '• Compliance with SEBI Do\'s & Don\'ts.',
        '• Consent to provide accurate KYC and client details as required.'
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <DocumentTextIcon className="w-16 h-16 mx-auto mb-6 text-blue-200" aria-hidden="true" focusable="false" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Most Important Terms and Conditions
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-blue-100">
              (MITC)
            </h2>
            <div className="bg-blue-700/30 rounded-lg p-6 max-w-5xl mx-auto mb-6">
              <p className="text-base md:text-lg text-blue-100 mb-2">
                (Forming part of the Terms & Conditions for providing research services)
              </p>
              <p className="text-lg md:text-xl text-gray-300 mb-4">
                In compliance with Regulation 24(6) of the SEBI (Research Analyst) Regulations, 2014 
                and SEBI Circular SEBI/HO/MIRSD/MIRSD-PoD-1/P/CIR/2025/004 dated January 08, 2025
              </p>
              <p className="text-lg font-semibold text-blue-100">
                SEBI Registered Research Analyst Registration No.: INH000023719
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-lg text-gray-300 leading-relaxed">
              The following are the Most Important Terms and Conditions (MITC) applicable to clients 
              availing research services from Analyzerview Market Research. These terms form an integral 
              part of our service agreement and must be understood before subscribing to our services.
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-1/4 bg-gray-700 p-6 flex flex-col items-center lg:items-start text-center lg:text-left">
                    <div className="w-12 h-12 bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                      <section.icon className="w-6 h-6 text-blue-400" aria-hidden="true" focusable="false" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">
                      {section.title}
                    </h3>
                  </div>
                  <div className="lg:w-3/4 p-6">
                    <ul className="space-y-3 list-none">
                      {section.content.map((paragraph, pIndex) => (
                        <li key={pIndex}>
                          {paragraph.startsWith('•') ? (
                            <div className="flex items-start space-x-2 ml-4">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                              <p className="text-gray-300 leading-relaxed">
                                {paragraph.substring(2)}
                              </p>
                            </div>
                          ) : paragraph.startsWith('📧') || paragraph.startsWith('📞') || paragraph.startsWith('🏢') ? (
                            <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 ml-4">
                              <p className="text-blue-300 font-medium">
                                {paragraph}
                              </p>
                            </div>
                          ) : paragraph.startsWith('Step') ? (
                            <p className="text-white font-semibold leading-relaxed">
                              {paragraph}
                            </p>
                          ) : paragraph.includes('Current fee limit:') ? (
                            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                              <p className="text-blue-300 font-semibold">
                                {paragraph}
                              </p>
                            </div>
                          ) : (
                            <p className="text-gray-300 leading-relaxed">
                              {paragraph}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Agreement Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-blue-700 bg-gray-900">
            <div className="text-center">
              <CheckCircleIcon className="w-16 h-16 text-blue-600 mx-auto mb-6" aria-hidden="true" focusable="false" />
              <h2 className="text-3xl font-bold text-white mb-6">
                Client Agreement
              </h2>
              <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold text-blue-200 mb-4">
                  By subscribing to our research services, you acknowledge and agree to all the terms outlined in this MITC document.
                </p>
                <p className="text-sm text-blue-300">
                  These terms are binding and form an integral part of your service agreement with Analyzerview Market Research.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mb-8">
                Please ensure that you have read, understood, and accepted all terms and conditions before proceeding with our research services. 
                If you have any questions or concerns, please contact our team for clarification.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
                >
                  Contact Us for Queries
                </a>
                <a
                  href="/pricing"
                  className="inline-flex items-center px-6 py-3 border border-blue-400 text-base font-medium rounded-md text-blue-400 bg-gray-800 hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
                >
                  View Pricing Plans
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-800 border-gray-700">
            <div className="text-center">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-blue-400 mx-auto mb-4" aria-hidden="true" focusable="false" />
              <h3 className="text-2xl font-bold text-white mb-6">
                Need Assistance?
              </h3>
              <ul className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto list-none">
                <li className="text-center">
                  <div className="text-2xl mb-2" aria-hidden="true">📧</div>
                  <div className="text-sm font-medium text-white">Email</div>
                  <div className="text-sm text-gray-300">rahul@analyzerview.com</div>
                </li>
                <li className="text-center">
                  <div className="text-2xl mb-2" aria-hidden="true">📞</div>
                  <div className="text-sm font-medium text-white">Phone</div>
                  <div className="text-sm text-gray-300">+91 93820 93168</div>
                </li>
                <li className="text-center">
                  <div className="text-2xl mb-2" aria-hidden="true">🏢</div>
                  <div className="text-sm font-medium text-white">Address</div>
                  <div className="text-sm text-gray-300">Kukshimla, Raidogachia, Purbasthali, Purba Bardhaman, West Bengal – 713513</div>
                </li>
              </ul>
            </div>
          </Card>
        </div>
      </section>
    </div>
  )
}

export default MITCPage